/**
 * Route.com Performance Optimizer
 * Conditionally loads Route only on cart page and when cart drawer is visible
 * Optimizes Core Web Vitals by preventing Route from loading on all pages
 */

(function() {
  'use strict';

  // Early intervention - block Route scripts immediately
  const originalAppendChild = Node.prototype.appendChild;
  const originalInsertBefore = Node.prototype.insertBefore;
  const blockedScripts = [];
  let routeBlocked = false;

  // Check if we should block Route (before DOM is ready)
  function shouldBlockRoute() {
    return window.location.pathname !== '/cart';
  }

  // Override appendChild to intercept script injection
  Node.prototype.appendChild = function(child) {
    if (child.tagName === 'SCRIPT' && shouldBlockRoute()) {
      const src = child.src || '';
      const content = child.textContent || '';
      
      if (src.includes('uirouter') || src.includes('route') || src.includes('routeapp') ||
          content.includes('uirouter') || content.includes('Route') || content.includes('route-app')) {
        console.log('Route script blocked:', src || 'inline script');
        blockedScripts.push({ element: child, parent: this });
        routeBlocked = true;
        return child; // Return without actually appending
      }
    }
    return originalAppendChild.call(this, child);
  };

  // Override insertBefore as well
  Node.prototype.insertBefore = function(child, referenceNode) {
    if (child.tagName === 'SCRIPT' && shouldBlockRoute()) {
      const src = child.src || '';
      const content = child.textContent || '';
      
      if (src.includes('uirouter') || src.includes('route') || src.includes('routeapp') ||
          content.includes('uirouter') || content.includes('Route') || content.includes('route-app')) {
        console.log('Route script blocked via insertBefore:', src || 'inline script');
        blockedScripts.push({ element: child, parent: this, reference: referenceNode });
        routeBlocked = true;
        return child;
      }
    }
    return originalInsertBefore.call(this, child, referenceNode);
  };

  const RouteOptimizer = {
    routeScripts: [],
    routeLoaded: false,
    cartDrawerVisible: false,
    blockedScripts: blockedScripts,
    
    // Check if we're on cart page
    isCartPage: function() {
      const isCartPath = window.location.pathname === '/cart';
      const hasCartClass = document.body && document.body.classList.contains('template-cart');
      return isCartPath || hasCartClass;
    },
    
    // Check if cart drawer is visible
    isCartDrawerVisible: function() {
      const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, .cart-drawer');
      if (!cartDrawer) return false;
      
      return cartDrawer.getAttribute('aria-hidden') === 'false' || 
             cartDrawer.classList.contains('open') ||
             cartDrawer.classList.contains('active') ||
             !cartDrawer.hidden;
    },
    
    // Store Route scripts for later loading
    interceptRouteScripts: function() {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
              const src = node.src || '';
              const content = node.textContent || '';
              
              // Detect Route scripts by common patterns
              if (src.includes('uirouter') || 
                  src.includes('route.') || 
                  content.includes('uirouter') ||
                  content.includes('Route') ||
                  src.includes('routeapp') ||
                  content.includes('route-app')) {
                
                // Store the script for later loading
                RouteOptimizer.routeScripts.push({
                  src: src,
                  content: content,
                  attributes: Array.from(node.attributes)
                });
                
                // Remove the original script if we're not on cart page/drawer
                if (!RouteOptimizer.isCartPage() && !RouteOptimizer.isCartDrawerVisible()) {
                  node.remove();
                  console.log('Route script blocked for performance optimization');
                }
              }
            }
          });
        });
      });
      
      observer.observe(document.head, { childList: true, subtree: true });
      observer.observe(document.body, { childList: true, subtree: true });
    },
    
    // Load Route scripts when needed
    loadRouteScripts: function() {
      if (this.routeLoaded) return;
      
      console.log('Loading Route scripts...');
      
      // Restore original methods temporarily
      Node.prototype.appendChild = originalAppendChild;
      Node.prototype.insertBefore = originalInsertBefore;
      
      // Load blocked scripts
      this.blockedScripts.forEach(function(scriptData) {
        if (scriptData.reference) {
          scriptData.parent.insertBefore(scriptData.element, scriptData.reference);
        } else {
          scriptData.parent.appendChild(scriptData.element);
        }
      });
      
      // Load any stored route scripts
      this.routeScripts.forEach(function(scriptData) {
        const script = document.createElement('script');
        
        // Restore attributes
        scriptData.attributes.forEach(function(attr) {
          script.setAttribute(attr.name, attr.value);
        });
        
        if (scriptData.src) {
          script.src = scriptData.src;
        }
        
        if (scriptData.content) {
          script.textContent = scriptData.content;
        }
        
        document.head.appendChild(script);
      });
      
      this.routeLoaded = true;
      
      // Initialize Route if it has an init function
      setTimeout(function() {
        if (window.uirouter && typeof window.uirouter.init === 'function') {
          window.uirouter.init();
        }
        if (window.Route && typeof window.Route.init === 'function') {
          window.Route.init();
        }
      }, 100);
    },
    
    // Set up cart drawer observers
    observeCartDrawer: function() {
      const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, .cart-drawer');
      if (!cartDrawer) return;
      
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'aria-hidden' || 
               mutation.attributeName === 'class' ||
               mutation.attributeName === 'hidden')) {
            
            const isVisible = RouteOptimizer.isCartDrawerVisible();
            
            if (isVisible && !RouteOptimizer.cartDrawerVisible) {
              RouteOptimizer.cartDrawerVisible = true;
              RouteOptimizer.loadRouteScripts();
            }
          }
        });
      });
      
      observer.observe(cartDrawer, { 
        attributes: true, 
        attributeFilter: ['aria-hidden', 'class', 'hidden'] 
      });
      
      // Also listen for cart drawer toggle events
      document.addEventListener('click', function(e) {
        const target = e.target;
        if (target.matches('[data-cart-toggle], .js-cart-toggle, .cart-toggle, [href="#cart-drawer"]') ||
            target.closest('[data-cart-toggle], .js-cart-toggle, .cart-toggle, [href="#cart-drawer"]')) {
          
          setTimeout(function() {
            if (RouteOptimizer.isCartDrawerVisible()) {
              RouteOptimizer.loadRouteScripts();
            }
          }, 50);
        }
      });
    },
    
    // Initialize the optimizer
    init: function() {
      const self = this;
      
      // Wait for DOM to be ready before doing anything
      function initializeOptimizer() {
        // Load immediately if on cart page
        if (self.isCartPage()) {
          console.log('Cart page detected - Route will load normally');
          return;
        }
        
        // Start intercepting scripts
        self.interceptRouteScripts();
        
        // Set up cart drawer observers
        self.observeCartDrawer();
        
        console.log('Route Performance Optimizer initialized');
      }
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeOptimizer);
      } else {
        initializeOptimizer();
      }
    }
  };
  
  // Start optimization immediately
  RouteOptimizer.init();
  
  // Export for debugging
  window.RouteOptimizer = RouteOptimizer;
})();